/**
 * @wl/widget-mortgage — Web Component для виджета ипотечного калькулятора.
 *
 * Регистрирует <wl-mortgage-widget> в customElements registry.
 * Использует Shadow DOM (mode:"open") для изоляции стилей от хост-сайта.
 *
 * Атрибуты:
 *   tenant       — slug тенанта (обязателен)
 *   theme        — "light-editorial" (MVP, default) | "dark-professional" (TODO Этап 2.1)
 *   brand-color  — hex-цвет (override --w-brand)
 *   config-url   — base URL API (default: https://widgets.wlabel.pro)
 *
 * Конфиг можно подать заранее через property:
 *   const el = document.createElement('wl-mortgage-widget');
 *   el.config = { ... };   // тогда fetch пропускается
 *
 * Embed-флоу (loader.js):
 *   1. loader fetch'ит конфиг → передаёт через el.config = cfg
 *   2. либо рендерит <wl-mortgage-widget tenant="..."> — компонент сам fetch'ит
 */

import { familyTerms, normalizeChildren } from '@wl/shared-ui/family-mortgage';
import type { WidgetConfig, ThemeId, LeadPayload } from './types';
import { widgetStyles, ensureFontsLoaded } from './styles';
import {
  renderTemplate,
  DEFAULT_RANGES,
  NO_OFFERS_DEFAULT_TEXT,
  bankLogoInner,
  escapeHtml,
  escapeAttr,
  safeColor,
} from './template';
import { formatPskRange, formatPskPct, formatRub, termSuffix } from './calculator';
import { LeadForm } from './lead-form';
import { formatPct } from './format';
import {
  bestPayment,
  computeOffers,
  firstOtherProgramWithOffers,
  leadSnapshot,
  loanFor,
  raiseDownToFit,
  type LeadSnapshot,
  type Offer,
} from './offers';
import {
  NO_OFFERS_TITLE,
  OTHER_PROGRAM_LABEL,
  familyAfterText,
  familyComboText,
  familyHintText,
  familyTierRates,
  familyUnavailableView,
  raiseDownLabel,
} from './family-text';
import { allOffersSame, droppedBanksText, moreLabelText, offerRateText } from './offer-text';

const DEFAULT_API_BASE = 'https://widgets.widgy.site';

/**
 * Подпись бейджа спецпредложения, когда клиент не задал свою. Живёт в виджете,
 * а не на сервере: текст по умолчанию — часть подачи, и дублировать его в двух
 * местах значит однажды поменять в одном и забыть про второе.
 */
const SPECIAL_BADGE_DEFAULT = 'Спецпредложение застройщика';

/** Дефолтный mock-конфиг — fallback если API недоступен. */
function fallbackConfig(tenant: string, brandColor: string | undefined): WidgetConfig {
  const cfg: WidgetConfig = {
    tenantId: tenant,
    theme: 'light-editorial',
    brandColor: brandColor ?? '#5B5FF5', // фирменный индиго widgy, пока клиент не задал свой
    defaultProgramId: 'family',
    // description больше не отображается в UI (Swiss Fintech показывает только name);
    // поле оставлено пустым — тип Program его требует, но это лишь offline-фолбэк.
    programs: [
      { id: 'family', name: 'Семейная', description: '' },
      { id: 'it', name: 'ИТ', description: '' },
      { id: 'military', name: 'Военная', description: '' },
      { id: 'standard', name: 'Стандартная', description: '' },
      { id: 'subsidized', name: 'Субсидия 11.9 %', description: '' },
    ],
    // Ставки на май 2026 — должны совпадать с packages/db/src/seed/igrotek.ts (BANKS).
    // Используются только если API недоступен (offline-фолбэк, не прод-сценарий).
    // У банков, не предоставляющих программу, ключ не задаём — UI рендерит «не предоставляется».
    //
    // «Семейная» по правилам с 01.10.2026: ставка берётся из сетки `family.terms`
    // (регион «вся Россия, кроме столичных»), а `rates.family` — лишь признак «банк
    // выдаёт программу», по контракту это ставка для defaultChildren (1 ребёнок → 10 %).
    // `combinedRatePct` — рыночная ставка банка для суммы сверх лимита программы;
    // в офлайн-демо берём её равной стандартной ставке этого же банка.
    banks: [
      { id: 'metallinvest', name: 'Металлинвестбанк', logoColor: '#004785', logoLetter: 'М', rates: { family: 10, it: 6.0, military: 19.5, standard: 18.8 }, combinedRatePct: 18.8 },
      { id: 'alfa', name: 'Альфа-Банк', logoColor: '#EF3124', logoLetter: 'А', rates: { family: 10, it: 6.0, military: 19.5, standard: 19.49, subsidized: 11.9 }, combinedRatePct: 19.49 },
      { id: 'vtb', name: 'ВТБ', logoColor: '#009FDF', logoLetter: 'В', rates: { family: 10, it: 6.0, military: 19.0, standard: 19.5 }, combinedRatePct: 19.5 },
      { id: 'sber', name: 'Сбербанк', logoColor: '#21A038', logoLetter: 'С', rates: { family: 10, it: 6.0, military: 19.1, standard: 20.0 }, combinedRatePct: 20.0 },
    ],
    family: { programId: 'family', terms: familyTerms('regions'), defaultChildren: 1 },
  };
  return cfg;
}


export class WlMortgageWidget extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['tenant', 'theme', 'brand-color', 'config-url'];
  }

  private _config: WidgetConfig | null = null;
  private _shadow: ShadowRoot;
  private _currentProgram: string = 'family';
  /** Число детей (1…5) и признак ребёнка-инвалида — состав семьи для «Семейной». */
  private _children = 1;
  private _disabledChild = false;
  /**
   * Банки, у которых раскрыта плашка условий в списке «Ещё N». Раскрытие идёт
   * НА МЕСТЕ строки — банк не переезжает в большую карточку, порядок выдачи не
   * меняется. Несколько строк можно держать открытыми одновременно: покупатель
   * сравнивает свой банк с соседним, не закрывая ни одного.
   *
   * Строки пересобираются в recalc через innerHTML, поэтому состояние живёт
   * здесь, а не в DOM, — иначе любое движение ползунка схлопывало бы плашки.
   */
  private _expandedBanks = new Set<string>();
  /**
   * Что делают кнопки пустого экрана «программа недоступна». Считается в recalc,
   * вместе с их подписями: клик читает готовое, а не пересчитывает заново.
   */
  private _noOffersActions: { raiseDpPct: number | null; otherProgramId: string | null } = {
    raiseDpPct: null,
    otherProgramId: null,
  };
  private _leadForm: LeadForm | null = null;
  private _cleanupFns: Array<() => void> = [];
  private _impressionSent = false;
  /**
   * Момент, когда виджет появился на странице (connectedCallback), а НЕ когда
   * открылась лид-форма — так же считают чат и витрина (commit 90285f3). Так
   * серверная ловушка ботов (leads.ts: submittedAt-mountedAt<3000мс) не задевает
   * живого покупателя: пока он заметит калькулятор, подвигает ползунки и откроет
   * форму, от монтажа виджета уже пройдёт больше трёх секунд — даже если поля
   * подставит автозаполнение браузера и он нажмёт «Отправить» за 2 секунды.
   */
  private _mountedAt = 0;

  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: 'open' });
  }

  // ── Public API: config через property ────────────────────────────────
  set config(cfg: WidgetConfig) {
    this._config = cfg;
    if (this.isConnected) this.safeRenderAndBind();
  }

  get config(): WidgetConfig | null {
    return this._config;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────
  connectedCallback(): void {
    this._mountedAt = Date.now();
    ensureFontsLoaded(document);
    this.setupIframeResizeReporter();
    if (this._config) {
      this.safeRenderAndBind();
      return;
    }
    void this.loadConfigAndRender();
  }

  disconnectedCallback(): void {
    this._leadForm?.destroy();
    this._leadForm = null;
    this._cleanupFns.forEach((fn) => fn());
    this._cleanupFns = [];
    this.setAttribute('data-wl-mounted', 'false');
  }

  /**
   * Когда виджет открыт в iframe-fallback (Tilda/Wix/Squarespace), сообщаем
   * родителю реальную высоту контента через postMessage('wl:resize'), иначе
   * loader держит фиксированный min-height 680px и на мобиле калькулятор режется.
   * Слушатель уже есть в packages/loader (renderIframeFallback). Вне iframe — no-op.
   */
  private setupIframeResizeReporter(): void {
    let framed = false;
    try {
      framed = typeof window !== 'undefined' && window.parent !== window.self;
    } catch {
      framed = true; // cross-origin доступ к parent кинул — значит точно в iframe
    }
    if (!framed || typeof ResizeObserver === 'undefined') return;

    const post = () => {
      const h = Math.ceil(
        Math.max(
          document.documentElement?.scrollHeight ?? 0,
          document.body?.scrollHeight ?? 0,
        ),
      );
      if (h > 0) {
        try {
          window.parent.postMessage({ type: 'wl:resize', height: h }, '*');
        } catch {
          /* ignore */
        }
      }
    };

    const ro = new ResizeObserver(() => post());
    ro.observe(document.documentElement);
    window.addEventListener('load', post);
    post();
    this._cleanupFns.push(() => {
      ro.disconnect();
      window.removeEventListener('load', post);
    });
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
    if (oldVal === newVal) return;
    if (!this.isConnected) return;
    if (name === 'brand-color' && newVal) {
      this.applyBrandColor(newVal);
    }
    if (name === 'theme' && newVal) {
      this._shadow.host.setAttribute('data-theme', newVal);
    }
    // tenant / config-url меняются крайне редко (фактически — пересоздание элемента);
    // полный re-fetch конфига оставляем на Этап 2.1, если потребуется.
  }

  // ── Config loading ───────────────────────────────────────────────────
  private async loadConfigAndRender(): Promise<void> {
    const tenant = this.getAttribute('tenant') ?? 'demo';
    const brandColor = this.getAttribute('brand-color') ?? undefined;

    // slug выбирает конкретный виджет одного kind (несколько объектов/ЖК у тенанта).
    // 'main' — канонический дефолт: loader ставит его при отсутствии data-slug.
    // Для 'main' (или пусто) НЕ шлём ?slug — бэкенд отдаёт старейший published
    // («main»), сохраняя поведение старых embed без data-slug. Реальный slug (≠main)
    // фильтрует именно свой виджет — так второй объект (Чайковский) отдаёт свой конфиг.
    const configEndpoint = this.buildConfigEndpoint();

    try {
      const res = await fetch(configEndpoint, {
        credentials: 'omit',
        ...within(12_000),
      });
      if (!res.ok) throw new Error(`config_${res.status}`);
      const cfg = (await res.json()) as WidgetConfig;
      this._config = {
        ...cfg,
        tenantId: cfg.tenantId ?? tenant,
        brandColor: brandColor ?? cfg.brandColor ?? '#5B5FF5',
        theme: (this.getAttribute('theme') as ThemeId | null) ?? cfg.theme ?? 'light-editorial',
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[wl-mortgage] config fetch failed, using fallback', err);
      this._config = fallbackConfig(tenant, brandColor);
    }
    this.safeRenderAndBind();
  }

  /**
   * Отрисовка под try (аудит отказоустойчивости 14.09.2026, п.4). В renderAndBind
   * ведут три пути, и из async-загрузки конфига исключение (например, mustQuery
   * лид-формы не нашла узел) уходило необработанным rejection'ом — калькулятор
   * оставался пустым местом без слова. У витрины тот же путь давно превращает
   * сбой в «Ошибка загрузки витрины».
   */
  private safeRenderAndBind(): void {
    try {
      this.renderAndBind();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wl-mortgage] render failed', err);
      this.renderFailure();
    }
  }

  private renderFailure(): void {
    try {
      this._leadForm?.destroy();
      this._leadForm = null;
      this._shadow.innerHTML =
        `<style>${widgetStyles}.wl-fail{padding:18px 20px;border:1px solid #E5E5E5;border-radius:14px;color:#5A5A5A;font-size:14px;line-height:1.5}</style>` +
        '<div class="wl-fail" role="status">Калькулятор временно недоступен. Обновите страницу чуть позже.</div>';
    } catch {
      // Не нарисовать даже строку — прячем: пустой прямоугольник на сайте хуже.
      this.style.display = 'none';
    }
  }

  /** URL конфиг-эндпоинта (он же выдаёт свежий embed-токен). Из атрибутов элемента. */
  private buildConfigEndpoint(): string {
    const tenant = this.getAttribute('tenant') ?? 'demo';
    const configUrl = this.getAttribute('config-url') ?? DEFAULT_API_BASE;
    const slug = this.getAttribute('data-slug')?.trim();
    return slug && slug !== 'main'
      ? `${configUrl}/api/public/widget/${tenant}/mortgage?slug=${encodeURIComponent(slug)}`
      : `${configUrl}/api/public/widget/${tenant}/mortgage`;
  }

  /**
   * Берёт СВЕЖИЙ embed-токен прямо перед отправкой лида. TTL токена — 15 мин,
   * а форма может висеть открытой дольше; поэтому не полагаемся на токен из
   * первоначального конфига (тем более при loader-инъекции его может не быть),
   * а тянем актуальный. Возвращает null при сетевой ошибке — тогда submit
   * честно покажет ошибку, а не сфабрикует успех.
   */
  private async fetchFreshEmbedToken(): Promise<string | null> {
    try {
      // cache:'no-store' ОБЯЗАТЕЛЕН: конфиг-эндпоинт кэшируется (ETag + s-maxage +
      // stale-while-revalidate). Без no-store браузер отдаёт из кэша/304 без свежего
      // токена → submit падает интермиттентно («не удалось отправить»). curl этого не
      // ловит — потому баг воспроизводился только в браузере.
      const res = await fetch(this.buildConfigEndpoint(), {
        credentials: 'omit',
        cache: 'no-store',
        ...within(12_000),
      });
      if (!res.ok) return null;
      const cfg = (await res.json()) as WidgetConfig;
      return typeof cfg.embedToken === 'string' && cfg.embedToken.length > 0
        ? cfg.embedToken
        : null;
    } catch {
      return null;
    }
  }

  /**
   * Спецпредложение застройщика показываем первым, когда оно выгоднее: его чип
   * встаёт в начало ряда и открыт при загрузке.
   *
   * Сравниваем по платежу на дефолтных значениях ползунков — это то, что
   * покупатель увидит в первую секунду. Если своё условие не выигрывает,
   * порядок не трогаем: двигать чип вперёд ради самого факта «оно наше» значит
   * подсовывать покупателю не лучшее предложение.
   */
  private promoteSpecialProgram(cfg: WidgetConfig): void {
    const hasSpecial = cfg.programs.some((p) => p.isSpecial);
    if (!hasSpecial) return;

    const price = cfg.ranges?.price?.default ?? DEFAULT_RANGES.price.default;
    const dpPct = cfg.ranges?.downPaymentPct?.default ?? DEFAULT_RANGES.downPaymentPct.default;
    const termYears = cfg.ranges?.term?.default ?? DEFAULT_RANGES.term.default;
    // Состав семьи на старте — тот, что увидит покупатель в первую секунду.
    const params = {
      price,
      dpPct,
      termYears,
      children: normalizeChildren(cfg.family?.defaultChildren),
      disabledChild: false,
    };

    // Лучший платёж программы на дефолтных ползунках — тем же расчётом оффера,
    // что и в recalc (порог взноса, ярус «Семейной», лимит), иначе первым встало
    // бы предложение, которое тут же исчезнет из списка или посчитано иначе.
    let champion: { id: string; payment: number } | null = null;
    let rivalBest = Infinity;
    for (const p of cfg.programs) {
      const pay = bestPayment(cfg, p.id, params);
      if (!Number.isFinite(pay)) continue;
      if (p.isSpecial) {
        if (!champion || pay < champion.payment) champion = { id: p.id, payment: pay };
      } else if (pay < rivalBest) {
        rivalBest = pay;
      }
    }
    if (!champion || champion.payment >= rivalBest) return;

    const winner = cfg.programs.find((p) => p.id === champion.id);
    if (!winner) return;
    cfg.programs = [winner, ...cfg.programs.filter((p) => p.id !== champion.id)];
    cfg.defaultProgramId = champion.id;
  }

  // ── Render + bindings ────────────────────────────────────────────────
  private renderAndBind(): void {
    if (!this._config) return;
    const cfg = this._config;

    // До renderTemplate: чипы рисуются по порядку cfg.programs и подсвечивают
    // defaultProgramId, поэтому переставлять их после отрисовки поздно.
    this.promoteSpecialProgram(cfg);

    this._shadow.host.setAttribute('data-theme', cfg.theme);
    // Слаг (а не UUID из конфига) нужен бейджу: по нему в Метрике читается,
    // какой клиент привёл трафик со своего сайта.
    const tenantSlug = (this.getAttribute('tenant') ?? '').trim();
    this._shadow.innerHTML = `<style>${widgetStyles}</style>${renderTemplate(cfg, tenantSlug)}`;

    if (cfg.brandColor) this.applyBrandColor(cfg.brandColor);

    // Подписка неактивна (демо истекло / оплаченный период кончился) → виджет
    // ПОЛНОСТЬЮ исчезает с сайта клиента: пустой рендер + host display:none (loader-
    // путь схлопывается). В iframe постим height:0, чтобы рамка тоже схлопнулась.
    // Возобновляется после оплаты при следующей загрузке страницы.
    if (cfg.active === false) {
      this.style.display = 'none';
      if (window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'wl:resize', height: 0 }, '*');
        } catch {
          /* cross-origin parent — ок */
        }
      }
      this.setAttribute('data-wl-mounted', 'true');
      this.dispatchEvent(
        new CustomEvent('wl:inactive', { bubbles: true, composed: true, detail: { tenantId: cfg.tenantId } }),
      );
      return;
    }

    this._currentProgram = cfg.defaultProgramId;
    this._children = normalizeChildren(cfg.family?.defaultChildren);
    this._disabledChild = false;

    this.bindSliders();
    this.bindChips();
    this.bindNoOffersActions();
    this.bindFamily();
    this.bindMore();
    this.bindOfferExpand();
    // Лид-форма — только если доступна по тарифу (server-driven).
    if (cfg.leadForm !== false) this.bindLeadForm();

    // Первый расчёт
    this.recalc();

    this.setAttribute('data-wl-mounted', 'true');
    this.dispatchEvent(
      new CustomEvent('wl:mounted', { bubbles: true, composed: true, detail: { tenantId: cfg.tenantId } }),
    );
    // Показ засчитываем только для активного (реально отрисованного) виджета —
    // на inactive-пути (cfg.active===false) выше стоит return, beacon не летит.
    this.sendImpression();
  }

  /**
   * Fire-and-forget beacon показа виджета → счётчик конверсии в кабинете.
   * Один раз на инстанс; только при реальном widgetId (fallback-конфиг без него
   * не считаем). Ошибки/оффлайн проглатываем — показ страницы клиента не трогаем.
   */
  private sendImpression(): void {
    if (this._impressionSent) return;
    const cfg = this._config;
    if (!cfg?.widgetId) return;
    this._impressionSent = true;
    const apiBase = this.getAttribute('config-url') ?? DEFAULT_API_BASE;
    // widgetId в query, без тела и без кастомных заголовков → «simple request»:
    // браузер НЕ делает CORS-preflight (иначе 2 запроса на каждый показ).
    const url = `${apiBase}/api/public/impression?widgetId=${encodeURIComponent(cfg.widgetId)}`;
    try {
      void fetch(url, { method: 'POST', keepalive: true, credentials: 'omit' }).catch(() => {});
    } catch {
      /* fire-and-forget */
    }
  }

  private applyBrandColor(hex: string): void {
    // Только 6-значный hex (darken/hexToRgba его и ожидают). Иначе — дефолт бренда.
    // Защита от CSS-инъекции через tenant-controlled brand-color / config.brandColor.
    const safe = /^#[0-9a-fA-F]{6}$/.test((hex ?? '').trim()) ? hex.trim() : '#5B5FF5';
    const host = this._shadow.host as HTMLElement;
    host.style.setProperty('--w-brand', safe);
    host.style.setProperty('--w-brand-hover', darken(safe, 0.12));
    host.style.setProperty('--w-brand-soft', hexToRgba(safe, 0.1));
    host.style.setProperty('--w-brand-focus', hexToRgba(safe, 0.2));
    // Осветлённый бренд для процентного сегмента бара/легенды — иначе при кастомном
    // brand-color «Проценты» остались бы дефолтно-зелёными.
    host.style.setProperty('--w-brand-100', hexToRgba(safe, 0.22));
  }

  private bindSliders(): void {
    const slPrice = this.$<HTMLInputElement>('[data-input="price"]');
    const slDp = this.$<HTMLInputElement>('[data-input="dp"]');
    const slTerm = this.$<HTMLInputElement>('[data-input="term"]');

    [slPrice, slDp, slTerm].forEach((sl) => {
      const handler = (): void => this.recalc();
      sl.addEventListener('input', handler);
      this._cleanupFns.push(() => sl.removeEventListener('input', handler));
    });
  }

  private bindChips(): void {
    const chips = this._shadow.querySelectorAll<HTMLButtonElement>('.wl-chip');
    chips.forEach((chip) => {
      const handler = (): void => {
        const programId = chip.dataset['program'];
        if (programId) this.selectProgram(programId);
      };
      chip.addEventListener('click', handler);
      this._cleanupFns.push(() => chip.removeEventListener('click', handler));
    });
  }

  /**
   * Переключить программу: чип, состояние, событие, пересчёт. Единственный путь —
   * его зовут и клик по чипу, и кнопка «Посмотреть другую программу» на пустом
   * экране, поэтому у них не может разойтись ни состояние, ни событие.
   * Возвращает чип выбранной программы (нужен, чтобы перевести на него фокус).
   */
  private selectProgram(programId: string): HTMLButtonElement | null {
    let picked: HTMLButtonElement | null = null;
    for (const c of Array.from(this._shadow.querySelectorAll<HTMLButtonElement>('.wl-chip'))) {
      const on = c.dataset['program'] === programId;
      c.classList.toggle('active', on);
      c.setAttribute('aria-pressed', String(on));
      if (on) picked = c;
    }
    this._currentProgram = programId;
    this.dispatchEvent(
      new CustomEvent('wl:program-changed', {
        bubbles: true,
        composed: true,
        detail: { programId },
      }),
    );
    this.recalc();
    return picked;
  }

  /**
   * Кнопки пустого экрана. Обе — обычные <button>: Tab, Enter и Space работают
   * без своего кода. Фокус после нажатия переводим на то, что изменилось (ползунок
   * взноса / чип программы): кнопка вместе с блоком исчезает, и без этого фокус
   * упал бы на <body> — клавиатурный пользователь потерял бы место.
   */
  private bindNoOffersActions(): void {
    const raise = this._shadow.querySelector<HTMLButtonElement>('[data-action="raise-down"]');
    const other = this._shadow.querySelector<HTMLButtonElement>('[data-action="other-program"]');
    const onRaise = (): void => {
      const dpPct = this._noOffersActions.raiseDpPct;
      if (dpPct === null) return;
      const slDp = this.$<HTMLInputElement>('[data-input="dp"]');
      slDp.value = String(dpPct);
      slDp.dispatchEvent(new Event('input', { bubbles: true }));
      slDp.focus({ preventScroll: true });
    };
    const onOther = (): void => {
      const id = this._noOffersActions.otherProgramId;
      if (id === null) return;
      const chip = this.selectProgram(id);
      chip?.focus({ preventScroll: true });
      // В узкой колонке чипы листаются вбок — выбранный должен оказаться в поле зрения.
      const bar = chip?.parentElement;
      if (chip && bar) {
        const c = chip.getBoundingClientRect();
        const b = bar.getBoundingClientRect();
        if (c.left < b.left || c.right > b.right) bar.scrollLeft += c.left - b.left - 16;
      }
    };
    raise?.addEventListener('click', onRaise);
    other?.addEventListener('click', onOther);
    this._cleanupFns.push(() => raise?.removeEventListener('click', onRaise));
    this._cleanupFns.push(() => other?.removeEventListener('click', onOther));
  }

  /**
   * Блок «Детей в семье»: радиогруппа 1…5+ и чекбокс «ребёнок с инвалидностью».
   * Клавиатура — по шаблону radiogroup из WAI-ARIA: Tab заходит на выбранную
   * кнопку, стрелки двигают выбор (выбор следует за фокусом), Home/End — к краям.
   */
  private bindFamily(): void {
    const seg = this._shadow.querySelector<HTMLElement>('[data-family-seg]');
    const check = this._shadow.querySelector<HTMLInputElement>('[data-family-disabled]');
    if (!seg || !check) return; // конфиг без config.family — блока нет

    const buttons = Array.from(seg.querySelectorAll<HTMLButtonElement>('[data-children]'));
    const select = (btn: HTMLButtonElement, focus: boolean): void => {
      const n = normalizeChildren(btn.dataset['children']);
      this._children = n;
      for (const b of buttons) {
        const on = b === btn;
        b.setAttribute('aria-checked', String(on));
        b.tabIndex = on ? 0 : -1;
      }
      if (focus) btn.focus();
      this.recalc();
    };

    const onClick = (e: Event): void => {
      const btn = (e.target as Element | null)?.closest<HTMLButtonElement>('[data-children]');
      if (btn) select(btn, false);
    };
    const onKeydown = (e: KeyboardEvent): void => {
      const cur = buttons.findIndex((b) => b.getAttribute('aria-checked') === 'true');
      let next = -1;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (cur + 1) % buttons.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (cur - 1 + buttons.length) % buttons.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = buttons.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      const target = buttons[next];
      if (target) select(target, true);
    };
    const onCheck = (): void => {
      this._disabledChild = check.checked;
      this.recalc();
    };

    seg.addEventListener('click', onClick);
    seg.addEventListener('keydown', onKeydown);
    check.addEventListener('change', onCheck);
    this._cleanupFns.push(() => seg.removeEventListener('click', onClick));
    this._cleanupFns.push(() => seg.removeEventListener('keydown', onKeydown));
    this._cleanupFns.push(() => check.removeEventListener('change', onCheck));
  }

  /** Тоггл collapsible «Ещё N предложений» (CSS grid-template-rows 0fr↔1fr). */
  private bindMore(): void {
    const btn = this._shadow.querySelector<HTMLButtonElement>('[data-action="toggle-more"]');
    const more = this._shadow.querySelector<HTMLElement>('[data-more]');
    const label = this._shadow.querySelector<HTMLElement>('[data-more-label]');
    if (!btn || !more || !label) return;
    const handler = (): void => {
      const open = more.getAttribute('data-open') === 'true';
      more.setAttribute('data-open', String(!open));
      btn.setAttribute('aria-expanded', String(!open));
      // Оба лейбла (с числом/словоформой) пересобираются в recalc и хранятся в dataset.
      label.textContent = open
        ? more.dataset['collapsedLabel'] ?? 'Ещё предложения'
        : more.dataset['openLabel'] ?? 'Свернуть предложения';
    };
    btn.addEventListener('click', handler);
    this._cleanupFns.push(() => btn.removeEventListener('click', handler));
  }

  /**
   * Раскрытие условий банка в списке «Ещё N» — плашка разворачивается НА МЕСТЕ
   * строки. Банк не переезжает наверх, порядок выдачи и лучший оффер не двигаются.
   *
   * Слушатель делегированный на shadow root: строки пересобираются в recalc через
   * innerHTML, прямой бинд по узлам терялся бы после первого движения ползунка.
   *
   * Лид-форму отсюда НЕ открываем — для заявки внутри плашки своя кнопка
   * ([data-offer-cta], её ловит lead-form.ts). Раньше строка вела прямо в анкету
   * захвата контактов, и покупатель, которому нужен был свой банк, упирался в неё
   * вместо ставки и ПСК.
   */
  private bindOfferExpand(): void {
    const root = this._shadow;

    const toggle = (head: HTMLElement): void => {
      const bankId = head.dataset['bank'];
      if (!bankId) return;
      const item = head.closest<HTMLElement>('[data-offer-item]');
      if (!item) return;
      const open = !this._expandedBanks.has(bankId);
      if (open) this._expandedBanks.add(bankId);
      else this._expandedBanks.delete(bankId);
      // Правим DOM точечно, без recalc: пересборка innerHTML сорвала бы CSS-переход
      // и увела фокус со строки, по которой человек только что нажал.
      item.dataset['expanded'] = open ? 'true' : 'false';
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.dispatchEvent(
        new CustomEvent('wl:offer-toggled', {
          bubbles: true,
          composed: true,
          detail: { bankId, expanded: open, programId: this._currentProgram },
        }),
      );
    };

    const onClick = (e: Event): void => {
      const target = e.target as Element | null;
      // Клик по кнопке заявки внутри плашки не должен её схлопывать.
      if (target?.closest('[data-offer-cta]')) return;
      const head = target?.closest<HTMLElement>('[data-offer-head]');
      if (head) toggle(head);
    };
    const onKeydown = (e: Event): void => {
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Enter' && ke.key !== ' ') return;
      const target = ke.target as Element | null;
      if (target?.closest('[data-offer-cta]')) return;
      const head = target?.closest<HTMLElement>('[data-offer-head]');
      if (!head) return;
      ke.preventDefault();
      toggle(head);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);
    this._cleanupFns.push(() => root.removeEventListener('click', onClick));
    this._cleanupFns.push(() => root.removeEventListener('keydown', onKeydown));
  }

  private bindLeadForm(): void {
    if (!this._config) return;
    const cfg = this._config;
    // submitEndpoint ВСЕГДА выводим из config-url (база API), игнорируя cfg.submitEndpoint:
    // tenant-controlled значение могло бы увести PII лидов на чужой сервер.
    const apiBase = this.getAttribute('config-url') ?? DEFAULT_API_BASE;
    const submitEndpoint = `${apiBase}/api/public/leads`;

    const bankNames: Record<string, string> = {};
    cfg.banks.forEach((b) => {
      bankNames[b.id] = b.name;
    });

    this._leadForm = new LeadForm({
      root: this._shadow,
      submitEndpoint,
      tenantId: cfg.tenantId,
      mountedAt: this._mountedAt,
      ...(cfg.widgetId ? { widgetId: cfg.widgetId } : {}),
      bankNames,
      getEmbedToken: () => this.fetchFreshEmbedToken(),
      getSnapshot: (bankId) => this.getSnapshot(bankId),
      onSuccess: (lead: LeadPayload) => {
        this.dispatchEvent(
          new CustomEvent('wl:lead-submitted', {
            bubbles: true,
            composed: true,
            detail: lead,
          }),
        );
      },
    });
  }

  // ── Recalculation ────────────────────────────────────────────────────
  private recalc(): void {
    if (!this._config) return;
    const cfg = this._config;
    // Опция «от»: платёж с приставкой «от» (показанная ставка — лучший случай). Дефолт off.
    const priceFrom = cfg.ui?.priceFrom === true;

    const slPrice = this.$<HTMLInputElement>('[data-input="price"]');
    const slDp = this.$<HTMLInputElement>('[data-input="dp"]');
    const slTerm = this.$<HTMLInputElement>('[data-input="term"]');

    const price = parseFloat(slPrice.value);
    const dpPct = parseFloat(slDp.value);
    const termYears = parseInt(slTerm.value, 10);

    // Взнос и сумма кредита — тем же расчётом, что в заявке и в сравнении программ.
    const { dpAmount, principal } = loanFor(price, dpPct);

    // Slider outputs
    this.$('[data-out="price"]').textContent = `${formatRub(price)} ₽`;
    this.$('[data-out="dp"]').textContent = `${formatRub(dpAmount)} ₽`;
    this.$('[data-out="dp-pct"]').textContent = formatPct(dpPct);
    this.$('[data-out="term"]').textContent = `${termYears} ${termSuffix(termYears)}`;

    // Fills
    this.updateFill(slPrice, this.$('[data-fill="price"]'));
    this.updateFill(slDp, this.$('[data-fill="dp"]'));
    this.updateFill(slTerm, this.$('[data-fill="term"]'));

    // ── Best-offer ранжирование ──────────────────────────────────────────
    const program = cfg.programs.find((p) => p.id === this._currentProgram);
    const progName = program?.name ?? '';

    // 1–2. Оферты всех банков по программе, отсортированные по платежу (лучший —
    //      первый). Расчёт один на весь виджет (offers.ts): банк без программы, порог
    //      взноса спецпредложения, ярус и лимит «Семейной», тай-брейки — внутри.
    const offerParams = {
      price,
      dpPct,
      termYears,
      children: this._children,
      disabledChild: this._disabledChild,
    };
    const { offers, quote, blocked, dropped } = computeOffers(cfg, this._currentProgram, offerParams);
    const family = cfg.family && cfg.family.programId === this._currentProgram ? cfg.family : null;
    this.renderFamilyBlock(family, quote, dpPct);

    const heroEl = this.$('[data-hero]') as HTMLElement;
    const moreEl = this.$('[data-more]') as HTMLElement;
    const noOffers = this.$('[data-no-offers]') as HTMLElement;
    this.$('[data-out="loan"]').textContent = `${formatRub(principal)} ₽`;

    // 3. Пустой случай — ни одного банка по программе.
    const droppedEl = this.$('[data-dropped]');
    if (offers.length === 0) {
      heroEl.hidden = true;
      moreEl.hidden = true;
      noOffers.hidden = false;
      droppedEl.hidden = true;
      this.renderNoOffers(cfg, offerParams, price, principal, blocked);
    } else {
      heroEl.hidden = false;
      noOffers.hidden = true;

      // 4. HERO = offers[0]. Лучший оффер остаётся на своём месте всегда: банки
      //    из списка раскрываются у себя в строке и наверх не переезжают.
      const best = offers[0]!;
      heroEl.dataset['bank'] = best.bank.id; // клик по hero → лид на лучший банк
      this.$('[data-hero-name]').textContent = best.bank.name;
      this.$('[data-hero-rate]').textContent = offerRateText(best);
      this.$('[data-hero-prog]').textContent = progName;
      this.$('[data-hero-pay]').textContent = (priceFrom ? 'от ' : '') + formatRub(best.payment);
      const logo = this.$('[data-hero-logo]') as HTMLElement;
      logo.style.background = safeColor(best.bank.logoColor, '#7B68A3');
      logo.innerHTML = bankLogoInner(best.bank);

      // Спецпредложение застройщика: бейдж + подсветка по периметру плашки.
      // Метим саму плашку атрибутом, а не классом на бейдже: подсветка — это
      // свойство карточки платежа, и она должна гаснуть при смене программы.
      const specialEl = this._shadow.querySelector<HTMLElement>('[data-hero-special]');
      const isSpecial = program?.isSpecial === true;
      heroEl.dataset['special'] = isSpecial ? 'true' : 'false';
      if (specialEl) {
        specialEl.textContent = program?.badgeLabel?.trim() || SPECIAL_BADGE_DEFAULT;
        specialEl.hidden = !isSpecial;
      }
      // «Семейная»: платёж после льготных 15 лет и разбивка суммы сверх лимита.
      this.setNote(
        this.$('[data-hero-after]'),
        best.family && family ? familyAfterText(best.family, family.terms) : null,
      );
      this.setNote(
        this.$('[data-hero-combo]'),
        best.family ? familyComboText(best.family, best.rate, best.bank.combinedRatePct) : null,
      );
      const heroPsk = this._shadow.querySelector<HTMLElement>('[data-hero-psk]');
      if (heroPsk) {
        // Банковский ПСК — раскрытие банка по продукту, не пересчёт под сумму
        // на ползунке. Поэтому «от X %» и ссылка на источник: показать его как
        // ПСК именно этого кредита было бы враньём. Верхнюю границу витрины не
        // берём — она одна на весь банк, а не на программу.
        heroPsk.textContent = best.pskFromBank
          ? `Полная стоимость кредита от ${formatPskPct(best.psk.min)} — по раскрытию банка`
          : `Полная стоимость кредита ${formatPskRange(best.psk)}`;
      }

      // 5. «★ Лучшее» + save-badge — только когда есть РЕАЛЬНО более выгодный банк
      //    (best строго дешевле второго). Если платежи равны — «лучшего» нет,
      //    крону и «Выгоднее на N ₽/мес» скрываем (просто показываем банки списком).
      const saveWrap = this.$('[data-hero-savewrap]') as HTMLElement;
      const bestBadge = this._shadow.querySelector<HTMLElement>('[data-hero-best]');
      const second = offers[1];
      const delta = second ? Math.round(second.payment - best.payment) : 0;
      const hasWinner = !!second && delta > 0;
      if (hasWinner) {
        this.$('[data-hero-save]').textContent = `Выгоднее на ${formatRub(delta)} ₽/мес`;
        saveWrap.hidden = false;
      } else {
        saveWrap.hidden = true;
      }
      if (bestBadge) bestBadge.hidden = !hasWinner;

      // 6. breakdown бар (по best-офферу). Блок опционален — клиент может скрыть
      // «из чего складывается выплата» из кабинета (не показывать переплату).
      const segBody = this._shadow.querySelector<HTMLElement>('[data-seg-body]');
      if (segBody) {
        // Для «Семейной» платёж меняется после 15 лет, поэтому «платёж × срок»
        // занизил бы переплату: берём то, что банк реально получит за весь срок.
        const totalPaid = best.totalPaid;
        const interest = Math.max(0, totalPaid - principal);
        let bodyPct = totalPaid > 0 ? (principal / totalPaid) * 100 : 50;
        bodyPct = Math.max(6, Math.min(94, bodyPct));
        segBody.style.flexBasis = `${bodyPct.toFixed(1)}%`;
        (this.$('[data-seg-int]') as HTMLElement).style.flexBasis = `${(100 - bodyPct).toFixed(1)}%`;
        this.$('[data-sum-body]').textContent = `${formatRub(principal)} ₽`;
        this.$('[data-sum-int]').textContent = `${formatRub(interest)} ₽`;
      }

      // 7. MORE = offers[1..] — перестраиваем innerHTML (состав меняется по программе).
      const rest = offers.slice(1);
      // Раскрытые плашки: банк мог выпасть из выдачи (сменилась программа, взнос
      // ушёл ниже порога спецпредложения) — снимаем его из набора, чтобы состояние
      // не копилось мусором и плашка не всплыла при возврате программы.
      const visible = new Set(offers.map((o) => o.bank.id));
      for (const id of this._expandedBanks) if (!visible.has(id)) this._expandedBanks.delete(id);
      if (rest.length === 0) {
        moreEl.hidden = true;
      } else {
        moreEl.hidden = false;
        // Все банки на одних условиях — не «Ещё 3 предложения», а «Те же условия ещё в 3 банках».
        // Список открыт с самого начала, и покупатель видит именно открытое состояние:
        // «Свернуть предложения» не сказало бы ему, что выбора тут нет. Поэтому при
        // одинаковых условиях заголовок один и тот же и в открытом виде, и в свёрнутом
        // (состояние читается по шеврону и aria-expanded).
        const same = allOffersSame(offers);
        const collapsedLabel = moreLabelText(rest.length, same);
        const openLabel = same ? collapsedLabel : 'Свернуть предложения';
        moreEl.dataset['collapsedLabel'] = collapsedLabel;
        moreEl.dataset['openLabel'] = openLabel;
        const open = moreEl.getAttribute('data-open') === 'true';
        const moreLabelEl = this.$('[data-more-label]');
        const wanted = open ? openLabel : collapsedLabel;
        if (moreLabelEl.textContent !== wanted) moreLabelEl.textContent = wanted;
        this.$('[data-more-inner]').innerHTML = rest
          .map((o) =>
            renderOfferRow(o, {
              priceFrom,
              expanded: this._expandedBanks.has(o.bank.id),
              programName: progName,
              principal,
              termYears,
              showPsk: cfg.ui?.showPsk !== false,
              showBreakdown: cfg.ui?.showBreakdown !== false,
              ctaLabel: cfg.ui?.ctaLabel ?? 'Создать заявку',
              leadForm: cfg.leadForm !== false,
              ...(family ? { family } : {}),
            }),
          )
          .join('');
      }

      // Банки, выпавшие из «Семейной» из-за суммы сверх лимита, не пропадают молча.
      this.setNote(droppedEl, droppedBanksText(dropped.map((b) => b.name)));
    }

    const best = offers[0];
    this.dispatchEvent(
      new CustomEvent('wl:calculation-updated', {
        bubbles: true,
        composed: true,
        detail: {
          price,
          downPayment: dpAmount,
          termYears,
          programId: this._currentProgram,
          bestBankId: best?.bank.id,
          // Банки с раскрытой плашкой условий — сигнал интереса к конкретному банку.
          expandedBankIds: [...this._expandedBanks],
          monthlyPayment: best ? Math.round(best.payment) : undefined,
          ...(family ? { familyChildren: quote?.children, familyDisabledChild: this._disabledChild } : {}),
        },
      }),
    );
  }

  private updateFill(slider: HTMLInputElement, fill: HTMLElement): void {
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const val = parseFloat(slider.value);
    const pct = ((val - min) / (max - min)) * 100;
    fill.style.width = `${pct}%`;
  }

  /**
   * Блок «Детей в семье»: виден только при выбранной «Семейной» (и только если
   * сервер прислал config.family). Пересчитывается при каждом расчёте: ставка на
   * каждой кнопке, пояснение, вторая строка про взнос от 50 %.
   * Пишем в DOM только изменившийся текст: пояснение — live-регион, и перезапись
   * того же текста заставляла бы скринридер зачитывать его на каждый сдвиг ползунка.
   */
  private renderFamilyBlock(
    family: WidgetConfig['family'] | null,
    quote: ReturnType<typeof computeOffers>['quote'],
    dpPct: number,
  ): void {
    const block = this._shadow.querySelector<HTMLElement>('[data-family]');
    if (!block) return;
    block.hidden = !family || !quote;
    if (!family || !quote) return;

    // Ставка яруса на каждой кнопке — тем же familyQuote, что и платёж.
    const rates = familyTierRates(family.terms, dpPct, this._disabledChild);
    for (const r of rates) {
      const btn = block.querySelector<HTMLElement>(`[data-children="${r.children}"]`);
      if (!btn) continue;
      const rateEl = btn.querySelector<HTMLElement>('[data-seg-rate]');
      const text = r.label.slice(r.label.indexOf('ставка ') + 'ставка '.length);
      if (rateEl && rateEl.textContent !== text) rateEl.textContent = text;
      if (btn.getAttribute('aria-label') !== r.label) btn.setAttribute('aria-label', r.label);
    }

    const hint = familyHintText(quote, family.terms);
    const main = this.$('[data-family-hint-main]');
    const tip = this.$('[data-family-hint-tip]');
    const note = this.$('[data-family-hint-note]');
    if (main.textContent !== hint.main) main.textContent = hint.main;
    this.setNote(tip, hint.tip);
    if (note.textContent !== hint.note) note.textContent = hint.note;
  }

  /**
   * Экран «программа недоступна»: заголовок с причиной, пояснение и две кнопки.
   *
   *   «Увеличить взнос до N ₽» — только если на шкале взноса есть положение, при
   *       котором кредит уложится в предел программы (иначе обещать нечего);
   *   «Посмотреть другую программу» — только если у какой-то другой программы на
   *       этих же параметрах есть предложения.
   */
  private renderNoOffers(
    cfg: WidgetConfig,
    params: { price: number; dpPct: number; termYears: number; children: number; disabledChild: boolean },
    price: number,
    principal: number,
    blocked: ReturnType<typeof computeOffers>['blocked'],
  ): void {
    const title = this.$('[data-no-offers-title]');
    const text = this.$('[data-no-offers-text]');
    const view = blocked
      ? familyUnavailableView(blocked.reason, blocked.loanCap, principal)
      : { title: NO_OFFERS_TITLE, text: NO_OFFERS_DEFAULT_TEXT };
    if (title.textContent !== view.title) title.textContent = view.title;
    if (text.textContent !== view.text) text.textContent = view.text;

    const raiseBtn = this.$<HTMLButtonElement>('[data-action="raise-down"]');
    const otherBtn = this.$<HTMLButtonElement>('[data-action="other-program"]');

    // Кнопка взноса: шкала берётся с самого ползунка — там уже учтены ranges клиента.
    let raise: { dpPct: number; dpAmount: number } | null = null;
    if (blocked) {
      const slDp = this.$<HTMLInputElement>('[data-input="dp"]');
      raise = raiseDownToFit(price, blocked.loanCap, {
        min: parseFloat(slDp.min),
        max: parseFloat(slDp.max),
        step: parseFloat(slDp.step),
      });
    }
    this._noOffersActions.raiseDpPct = raise ? raise.dpPct : null;
    raiseBtn.hidden = raise === null;
    if (raise) {
      const label = raiseDownLabel(raise.dpAmount);
      if (raiseBtn.textContent !== label) raiseBtn.textContent = label;
    }

    const otherId = firstOtherProgramWithOffers(cfg, this._currentProgram, params);
    this._noOffersActions.otherProgramId = otherId;
    otherBtn.hidden = otherId === null;
    if (otherId !== null) {
      const name = cfg.programs.find((p) => p.id === otherId)?.name ?? '';
      otherBtn.setAttribute('aria-label', `${OTHER_PROGRAM_LABEL}: ${name}`);
    }
  }

  /** Строка-примечание: показывает текст или прячет элемент, если сказать нечего. */
  private setNote(el: HTMLElement, text: string | null): void {
    el.hidden = text === null;
    if (text !== null && el.textContent !== text) el.textContent = text;
  }

  /**
   * Снимок текущих параметров для lead payload. Платёж и банк — из того же
   * computeOffers, что и выдача на экране (hero = лучший оффер), а состав семьи
   * уходит только по «Семейной». Банк конкретной заявки задаёт LeadForm.open(bankId) —
   * кнопка в плашке банка или клик по hero: платёж считается для него.
   */
  private getSnapshot(bankId?: string): LeadSnapshot {
    const slPrice = this.$<HTMLInputElement>('[data-input="price"]');
    const slDp = this.$<HTMLInputElement>('[data-input="dp"]');
    const slTerm = this.$<HTMLInputElement>('[data-input="term"]');
    return leadSnapshot(
      this._config,
      this._currentProgram,
      {
        price: parseFloat(slPrice.value),
        dpPct: parseFloat(slDp.value),
        termYears: parseInt(slTerm.value, 10),
        children: this._children,
        disabledChild: this._disabledChild,
      },
      bankId,
    );
  }

  // ── DOM helper ───────────────────────────────────────────────────────
  private $<T extends Element = HTMLElement>(selector: string): T {
    const el = this._shadow.querySelector<T>(selector);
    if (!el) throw new Error(`[wl-mortgage] missing element: ${selector}`);
    return el;
  }
}

// ── Utils ──────────────────────────────────────────────────────────────

/**
 * Потолок ожидания запроса. Без него подвисший (не упавший) API держал бы
 * конфиг калькулятора в бесконечной загрузке — тот же приём и тот же случай,
 * что уже чинили в чате и витрине (commit 90285f3, аудит 14.09.2026). Старый
 * браузер без AbortSignal.timeout ведёт себя как раньше.
 */
function within(ms: number): { signal?: AbortSignal } {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? { signal: AbortSignal.timeout(ms) }
    : {};
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darken(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

interface OfferRowOpts {
  priceFrom: boolean;
  /** Плашка условий раскрыта (состояние живёт в _expandedBanks, не в DOM). */
  expanded: boolean;
  programName: string;
  principal: number;
  termYears: number;
  showPsk: boolean;
  showBreakdown: boolean;
  ctaLabel: string;
  leadForm: boolean;
  /** Условия «Семейной» — нужны подписи про срок субсидии. Нет у обычных программ. */
  family?: NonNullable<WidgetConfig['family']>;
}

/**
 * Строка оффера в списке «Ещё N» — шапка + плашка условий, раскрывающаяся НА МЕСТЕ.
 *
 * Клик по шапке разворачивает условия этого банка прямо в строке: банк остаётся
 * там, где стоит по выдаче, порядок и лучший оффер не двигаются. Лид-форму шапка
 * НЕ открывает — для заявки внутри плашки своя кнопка. Раньше строка вела прямо
 * в анкету захвата контактов, и покупатель, которому нужен был свой банк, не мог
 * посмотреть ни ставку, ни ПСК.
 *
 * Высота плашки анимируется через grid-template-rows 0fr↔1fr — тем же приёмом,
 * что и список «Ещё N» целиком, без замера высоты в JS.
 */
function renderOfferRow(o: Offer, opts: OfferRowOpts): string {
  const { priceFrom, expanded, programName, principal, termYears } = opts;
  const bankId = escapeAttr(o.bank.id);
  const panelId = `wl-offer-panel-${bankId}`;

  // Переплата — от того, что банк реально получит за весь срок (у «Семейной»
  // платёж после 15 лет выше, «платёж × срок» её занизил бы).
  const interest = Math.max(0, o.totalPaid - principal);

  const pskLine = opts.showPsk
    ? `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Полная стоимость кредита</span><span class="wl-offer-fact-v">${escapeHtml(
        o.pskFromBank
          ? `от ${formatPskPct(o.psk.min)} — по раскрытию банка`
          : formatPskRange(o.psk),
      )}</span></div>`
    : '';

  const breakdown = opts.showBreakdown
    ? `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Тело кредита</span><span class="wl-offer-fact-v">${formatRub(principal)} ₽</span></div>` +
      `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Проценты за срок</span><span class="wl-offer-fact-v">${formatRub(interest)} ₽</span></div>`
    : '';

  // «Семейная»: платёж после льготных лет и разбивка суммы сверх лимита.
  const afterText = o.family && opts.family ? familyAfterText(o.family, opts.family.terms) : null;
  const comboText = o.family ? familyComboText(o.family, o.rate, o.bank.combinedRatePct) : null;
  const familyNotes =
    (afterText ? `<p class="wl-offer-note wl-offer-note--after">${escapeHtml(afterText)}</p>` : '') +
    (comboText ? `<p class="wl-offer-note">${escapeHtml(comboText)}</p>` : '');

  // Заявка именно по этому банку. role="button" + data-bank — ровно то, что ловит
  // делегированный listener лид-формы.
  const cta = opts.leadForm
    ? `<button type="button" class="wl-offer-cta" data-offer-cta data-bank="${bankId}" role="button" aria-label="${escapeAttr(`${o.bank.name} — ${opts.ctaLabel}`)}">${escapeHtml(opts.ctaLabel)}</button>`
    : '';

  return (
    `<div class="wl-offer-item" data-offer-item data-bank="${bankId}" data-expanded="${expanded ? 'true' : 'false'}">` +
    `<div class="wl-offer" data-offer-head data-bank="${bankId}" tabindex="0" role="button" aria-expanded="${expanded ? 'true' : 'false'}" aria-controls="${panelId}" aria-label="${escapeAttr(`${o.bank.name} — условия`)}">` +
    `<div class="wl-offer-logo" style="background:${safeColor(o.bank.logoColor, '#7B68A3')}" aria-hidden="true">${bankLogoInner(o.bank)}</div>` +
    `<div class="wl-offer-mid">` +
    `<div class="wl-offer-name">${escapeHtml(o.bank.name)}</div>` +
    // Программу в строке НЕ дублируем: на 375 px подпись уезжала во вторую строку
    // и отжимала название банка в многоточие. Программа видна в активном чипе и
    // внутри раскрытой плашки.
    `<div class="wl-offer-rate">ставка ${escapeHtml(offerRateText(o))}</div>` +
    `</div>` +
    `<div class="wl-offer-price">${priceFrom ? 'от ' : ''}${formatRub(o.payment)} <span>₽/мес</span></div>` +
    `<svg class="wl-offer-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
    `</div>` +
    `<div class="wl-offer-panel" id="${panelId}">` +
    `<div class="wl-offer-panel-inner">` +
    `<div class="wl-offer-facts">` +
    // Платёж, сумма и срок показываем всегда: тумблеры showPsk/showBreakdown —
    // право клиента спрятать ПСК и переплату, но тогда раскрывать было бы нечего.
    `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Ежемесячный платёж</span><span class="wl-offer-fact-v">${formatRub(o.payment)} ₽</span></div>` +
    `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Ставка</span><span class="wl-offer-fact-v">${escapeHtml(offerRateText(o))} · ${escapeHtml(programName)}</span></div>` +
    `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Сумма кредита</span><span class="wl-offer-fact-v">${formatRub(principal)} ₽</span></div>` +
    `<div class="wl-offer-fact"><span class="wl-offer-fact-k">Срок</span><span class="wl-offer-fact-v">${termYears} ${termSuffix(termYears)}</span></div>` +
    pskLine +
    breakdown +
    familyNotes +
    `</div>` +
    cta +
    `</div>` +
    `</div>` +
    `</div>`
  );
}

// Self-register ──────────────────────────────────────────────────────────
if (typeof customElements !== 'undefined' && !customElements.get('wl-mortgage-widget')) {
  customElements.define('wl-mortgage-widget', WlMortgageWidget);
}

export type { WidgetConfig, Program, Bank, ThemeId } from './types';
export { DEFAULT_RANGES };
